import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchUser, fetchGigs, fetchReviews } from "@/lib/api";
import GigCard from "@/components/GigCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AvatarUploader } from "@/components/AvatarUploader";

export default function SellerProfilePage() {
  const { id } = useParams();
  const [seller, setSeller] = useState(null);
  const [gigs, setGigs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { user } = useAuth();
  const isMe = user?.user_id === id;

  useEffect(() => {
    fetchUser(id).then(setSeller).catch(() => setSeller(false));
    fetchGigs({ seller_id: id }).then(setGigs).catch(() => {});
    fetchReviews({ seller_id: id }).then(setReviews).catch(() => {});
  }, [id]);

  if (seller === false) return <div className="p-16 text-center">Seller not found.</div>;
  if (!seller) return <div className="p-16 text-center">Loading…</div>;

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <div className="grid lg:grid-cols-[320px_1fr] gap-10 mb-10">
        <div className="border border-foreground/15 p-6 brutal-shadow-sm bg-card">
          <div className="flex flex-col items-start">
            {isMe ? (
              <div className="mb-4">
                <AvatarUploader showText={false}/>
                <div data-testid="avatar-change-hint" className="text-xs text-muted-foreground mt-2">Click your photo to change it</div>
              </div>
            ) : seller.picture ? (
              <img src={seller.picture} alt={seller.name} className="w-24 h-24 rounded-full object-cover border-2 border-foreground mb-4"/>
            ) : (
              <div className="w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl font-bold mb-4">{seller.name?.[0]}</div>
            )}
            <h1 data-testid="seller-name" className="text-2xl font-bold tracking-tight flex items-center gap-2">
              {seller.name}
              <VerifiedBadge verified={seller.verified} size={5}/>
            </h1>
            <div className="text-sm text-muted-foreground mono mt-1">{seller.email}</div>
            <div className="flex items-center gap-1.5 mt-3">
              <Star className="w-4 h-4 fill-primary text-primary"/>
              <span className="font-medium">{seller.rating_avg?.toFixed(1) || "New"}</span>
              <span className="text-sm text-muted-foreground">({seller.rating_count} reviews)</span>
            </div>
          </div>
          {seller.bio && <p className="text-sm text-foreground/80 mt-5 border-t border-foreground/10 pt-4">{seller.bio}</p>}
          {seller.skills?.length > 0 && (
            <div className="mt-5">
              <div className="overline mb-2">◆ Skills</div>
              <div className="flex flex-wrap gap-1.5">
                {seller.skills.map((s) => (
                  <span key={s} className="text-xs mono border border-foreground/20 px-2 py-1 uppercase">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="overline text-primary mb-3">◆ ACTIVE GIGS</div>
          <h2 className="text-3xl font-bold tracking-tight mb-8">{gigs.length} gigs by {seller.name}</h2>
          {gigs.length === 0 ? (
            <div className="border border-dashed border-foreground/20 p-12 text-center text-muted-foreground">
              No active gigs yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {gigs.map((g, i) => <GigCard key={g.gig_id} gig={g} testId={`seller-gig-${i}`}/>)}
            </div>
          )}

          {reviews.length > 0 && (
            <div className="mt-14">
              <div className="overline text-primary mb-3">◆ REVIEWS</div>
              <h2 className="text-2xl font-bold tracking-tight mb-6">What buyers say</h2>
              <div className="grid md:grid-cols-2 gap-5">
                {reviews.slice(0, 6).map((r) => (
                  <div key={r.review_id} className="border border-foreground/10 p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="font-medium">{r.buyer_name}</div>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} className={`w-3 h-3 ${n <= r.rating ? "fill-primary text-primary" : "text-muted"}`}/>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-foreground/80">{r.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
